import { Component, OnDestroy, OnInit, inject, ElementRef, ViewChild, AfterViewInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterOutlet } from '@angular/router';
import { Subscription } from 'rxjs';

// Import the header and sidebar components
import { HeaderComponent } from './header/header.component';
import { SidebarComponent } from './sidebar/sidebar.component';
import { MaterialModule } from '@app/material.module';
import { IdleService } from '@app/shared/idle.service';
import { SignalrService } from '@app/core/services/signalr.service';
import { ProfileService } from '@app/shared/profile/services/profile.service';
import { ConstantsService } from '@app/core/services/constants.service';
import { AuthService } from '@app/core/services/auth.service';
import { ImageOptimizationService } from '@app/core/services/image-optimization.service';
import { RcService } from '@app/core/services/rc.service';

declare var $: any;
@Component({
  selector: 'app-layouts',
  templateUrl: './layouts.component.html',
  styleUrls: ['./layouts.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    MaterialModule,
    HeaderComponent,
    SidebarComponent
  ]
})

export class LayoutsComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('layoutElement', { static: false }) layoutElement!: ElementRef<HTMLElement>;

  private readonly userSubscription: Subscription;
  idleService = inject(IdleService);
  private idleSubscription?: Subscription
  isModalOpen: boolean = false;
  isIdeallogOut: boolean = false;

  userData: any;
  employeeNumber!: number;
  theme!: string;
  previousTheme?: string;

  settingsSubscription!: Subscription;

  isSupervisor: boolean = false;
  isReviewer: boolean = false;
  isModalOpenCloseConcurrentLogin: boolean = false;
  progressPercentage: number = 100;
  timer: number = 10;

  private concurrentLoginCheckInterval: any;
  private connectedEmployeeNumber: number | string | null = null;

  constructor(
    private readonly signalrService: SignalrService,
    private readonly profileService: ProfileService,
    private readonly constantsService: ConstantsService,
    private readonly authService: AuthService,
    private readonly imageOptimizationService: ImageOptimizationService,
    private readonly router: Router,
    public rcService: RcService
  ) {
    this.userSubscription = this.authService.currentUser$.subscribe(user => {
      this.userData = user;
      this.employeeNumber = user?.credentials?.EmployeeNumber;
      this.isSupervisor = user?.isSupervisor;
      this.isReviewer = user?.canReviewFirstLevel;

      if (!user?.credentials?.EmployeeNumber) {
        this.connectedEmployeeNumber = null;
        return;
      }

      const employeeNumber = user.credentials.EmployeeNumber;
      if (employeeNumber !== this.connectedEmployeeNumber) {
        this.connectedEmployeeNumber = employeeNumber;
        this.signalrService.initializeSignalRConnection(user);
        this.signalrService.initializeInventorySignalRConnection(user);
      }
    });

    if (this.isSupervisor || this.isReviewer) {
      this.settingsSubscription = this.profileService.settings$.subscribe(newSettings => {
        this.previousTheme = this.theme;
        this.theme = newSettings?.find((itm: any) => itm.ProfileTypeID == 3)?.SettingValue;
        if (this.theme === undefined) {
          // Subscribe to HomeStateService profile data instead of making direct API call
          this.getProfileSettings();
        } else if (this.theme === 'seasonal') {
          this.theme = this.constantsService.getCurrentSeason();
        }

        // Apply optimized background image when theme changes
        if (this.previousTheme !== this.theme && this.layoutElement) {
          this.applyOptimizedBackgroundImage();
        }
      });
    } else {
      this.theme = this.constantsService.getCurrentSeason();
    }
    this.initListener();

    // Preload all theme images for better performance
    this.preloadBackgroundImages();
  }
  openModalOpenCloseConcurrentLogin() {
    this.isModalOpenCloseConcurrentLogin = true;
  }


  ngAfterViewInit() {
    if (this.userData && this.userData.ConcurrentLogin) {
      this.authService.checkActiveSessions(this.userData.credentials.EmployeeNumber).subscribe((d) => {
        if (!d) {
          this.isModalOpenCloseConcurrentLogin = true;
        }
      });
    }

    // Apply optimized background image after view initialization
    setTimeout(() => {
      this.applyOptimizedBackgroundImage();
    });
  }
  ngOnInit(): void {
    this.idleSubscription = this.idleService.idleState.subscribe((isIdle) => {
      if (isIdle) {
        this.isModalOpen = true;
        this.startProgressCountdown();
        this.isIdeallogOut = true;
        this.progressPercentage = 100;
        this.timer = 10;
      }
    });

    // Refresh tenant data whenever the authenticated layout loads (covers page reload
    // and direct navigation scenarios where the login page was never rendered).
    this.authService.getSingleTenant().subscribe((d) => {
      if (d?.Root && d?.Image) {
        this.rcService.setTenantData({
          Name: d.Name,
          DisplayName: d.DisplayName,
          Root: d.Root,
          Image: d.Image,
          ImageWidth: d.ImageWidth,
          ImageHeight: d.ImageHeight,
          BuildDate: d.BuildDate,
          env: d.env
        });
      }
    });

    // Periodically check for concurrent logins
    this.startConcurrentLoginCheck();
  }

  startProgressCountdown(): void {
    const interval = setInterval(() => {

      if (this.isModalOpen) {
        if (this.progressPercentage > 0) {
          this.progressPercentage -= (1 * +this.timer);
        } else {
          if (this.isIdeallogOut) {
            clearInterval(interval);
            // Handle what should happen when the progress reaches 0, e.g., auto logout
            this.logout(); // Automatically log out when progress reaches 0
          }
        }
      }
    }, 1000); // Decrease by 1% every second

  }

  stay(): void {
    this.idleService.resetTimer();
    this.isModalOpen = false;
    this.isIdeallogOut = false;
    this.progressPercentage = 100;
    this.timer = 10;
  }

  CloseConcurrentLogin(): void {
    this.authService.clearUserData();
    this.router.navigate([`/${this.rcService.getTenant()}/auth/login`]);
  }
  logout(): void {
    this.authService.clearUserData();
    this.router.navigate([`/${this.rcService.getTenant()}/auth/login`]);
  }

  private initListener(): void {
    document.addEventListener('mousemove', () => {
      this.idleService.resetTimer();
    });
    document.addEventListener('keypress', () => {
      this.idleService.resetTimer();
    });
  }

  ngOnDestroy(): void {
    this.userSubscription?.unsubscribe();
    if (this.idleSubscription) {
      this.idleSubscription?.unsubscribe();
    }
    if (this.concurrentLoginCheckInterval) {
      clearInterval(this.concurrentLoginCheckInterval);
    }
  }

  getProfileSettings(): void {
    // Only get profile settings if we don't have them in localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        let profileData = user?.profile?.length > 0 && !Array.isArray(user?.profile)
          ? JSON.parse(user?.profile)
          : user?.profile;

        if (profileData && Array.isArray(profileData)) {
          let currentTheme = profileData.find((setting: { ProfileTypeID: number; }) => setting.ProfileTypeID === 3)?.SettingValue;
          if (currentTheme) {
            this.theme = currentTheme;
            if (this.theme === 'seasonal') {
              this.theme = this.constantsService.getCurrentSeason();
            }
            return; // Exit early if we found the theme in localStorage
          }
        }
      } catch (error) {
        console.error('Error parsing user data from localStorage:', error);
      }
    }

    // If we couldn't get the theme from localStorage, use default seasonal theme
    this.theme = this.constantsService.getCurrentSeason();
  }

  private startConcurrentLoginCheck(): void {
    // Check every 30 seconds for concurrent logins
    const interval = setInterval(() => {
      if (this.userData && this.userData.credentials && this.userData.credentials.EmployeeNumber && this.userData.ConcurrentLogin) {
        this.authService.checkActiveSessions(this.userData.credentials.EmployeeNumber, true).subscribe({
          next: (isValid) => {
            if (!isValid) {
              // Session is no longer valid, likely due to concurrent login
              this.isModalOpenCloseConcurrentLogin = true;
              this.signalrService.showConcurrentLoginDialog();
            }
          },
          error: (error) => {
            console.error('Error checking active sessions:', error);
          }
        });
      }
    }, 30000); // Check every 30 seconds

    // Store the interval ID so we can clear it when the component is destroyed
    this.concurrentLoginCheckInterval = interval;
  }

  /**
   * Apply optimized background image based on current theme
   */
  private applyOptimizedBackgroundImage(): void {
    if (this.layoutElement && this.theme) {
      this.imageOptimizationService.applyBackgroundImage(
        this.layoutElement.nativeElement,
        this.theme
      );
    }
  }

  /**
   * Preload background images for better performance
   */
  private async preloadBackgroundImages(): Promise<void> {
    try {
      const isMobile = this.imageOptimizationService.isMobileDevice();
      await this.imageOptimizationService.preloadAllThemes(isMobile);

      // Preload current theme first if available
      if (this.theme) {
        await this.imageOptimizationService.preloadImage(this.theme, isMobile);
      }
    } catch (error) {
      console.warn('Background image preloading failed:', error);
    }
  }

}
